import React from 'react';
import { useStyletron } from 'baseui';
import _get from 'lodash/get';

import Label from '../baseui/input/Label';
import useDependencies from '../../customHooks/useDependencies';
import { getCurrentPath, getCurrentDefaultValue } from './helper';
import ErrorMessage from '../ErrorMessage';
import { ERROR_SEVERITY } from '../../constants';

interface IInputRenderer {
	field: any;
	control: any;
	errors?: any;
	mode?: any;
	watch: any;
	setShowLoader?: any;
	defaultValue?: any;
	setValue?: any;
	initialValues?: any;
}

const useInputRenderer = ({
	field,
	control,
	errors,
	mode,
	watch,
	setValue,
	setShowLoader,
	defaultValue,
	initialValues
}: IInputRenderer) => {
	const InputComponent = field.renderer;
	const hidden = _get(field.getRenderProps(), ['isHidden', mode], false);
	const currentPath = getCurrentPath(_get(field, 'path', []), field.name);
	const currentDefaultValue = getCurrentDefaultValue(defaultValue, field.name);
	const disabled = !_get(field.getRenderProps(), ['isEditable', mode], true);
	const label = field.getLabel();
	const inputWidth = _get(field.getRenderProps(), 'inputWidth', 500);
	const name = currentPath.join('.');
	const error = _get(errors, name);
	const toolTip = field.getToolTip();
	const description = field.getDescription();
	const minHeight = _get(field.getRenderProps(), 'minHeight', '100px');
	return {
		InputComponent,
		field,
		control,
		mode,
		watch,
		hidden,
		inputWidth,
		name,
		label,
		error,
		disabled,
		currentDefaultValue,
		setShowLoader,
		toolTip,
		setValue,
		initialValues,
		minHeight,
		description
	};
};

const InputRenderer: React.FC<IInputRenderer> = (props) => {
	const {
		InputComponent,
		field,
		control,
		mode,
		watch,
		hidden,
		name,
		label,
		error,
		disabled,
		currentDefaultValue,
		setShowLoader,
		toolTip,
		setValue,
		initialValues,
		minHeight,
		description
	} = useInputRenderer(props);
	const [css] = useStyletron();
	const inputProps = useDependencies({
		...field,
		...field.getRenderProps(),
		name,
		disabled,
		control,
		mode,
		watch,
		hidden,
		label,
		toolTip,
		setShowLoader,
		defaultValue: currentDefaultValue,
		InputComponent,
		setValue,
		initialValues,
		minHeight,
		description
	});

	const wrapperClassName = _get(inputProps, 'noWrapper', false)
		? undefined
		: css({ border: '1px solid #e0e0e0', width: '100%', padding: '10px' });

	const { InputComponent: Component } = inputProps;

	return !inputProps.hidden ? (
		<div className={wrapperClassName}>
			{inputProps.label && (
				<Label
					label={inputProps.label}
					toolTip={inputProps.toolTip}
					required={inputProps.required && !inputProps.disabled}
					description={inputProps.description}
				/>
			)}
			<Component {...inputProps} />
			{error?.message && <ErrorMessage severity={ERROR_SEVERITY.ERROR} message={error.message} />}
		</div>
	) : null;
};

export default InputRenderer;
